import { useState } from "react"
import { XIcon } from "lucide-react"
import { EditableText } from "@/components/editable-text/editable-text"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { languageName, localize, localized, useLanguage } from "./language"
import { ModelSelect } from "./modelselect"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  playbook: Playbook
  onChange: (playbook: Playbook) => void
}

function canonical(code: string) {
  try { return Intl.getCanonicalLocales(code.trim())[0] } catch { return undefined }
}

export function PlaybookSettingsDialog({ open, onOpenChange, playbook, onChange }: Props) {
  const display = useLanguage()
  const [noMatchLanguage, setNoMatchLanguage] = useState(display)
  const noMatch = playbook.defaults?.on_no_match ?? {}
  const [defaultDraft, setDefaultDraft] = useState<string>()
  const [draft, setDraft] = useState("")
  const language = playbook.config?.language || "en"
  const languages = playbook.config?.languages ?? []
  const added = canonical(draft)
  const canAdd = !!added && added !== language && !languages.includes(added)

  function setConfig(patch: PlaybookConfig) {
    onChange({ ...playbook, config: { ...playbook.config, ...patch } })
  }

  function nonEmpty(next: string[]) {
    return next.length ? next : undefined
  }

  function commitDefault() {
    const code = defaultDraft === undefined ? undefined : canonical(defaultDraft)
    if (code && code !== language) setConfig({ language: code, languages: nonEmpty(languages.filter(l => l !== code)) })
    setDefaultDraft(undefined)
  }

  function add() {
    if (!canAdd) return
    setConfig({ languages: [...languages, added] })
    setDraft("")
  }

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Playbook settings</DialogTitle>
      </DialogHeader>
      <Tabs defaultValue="general" className="gap-4">
        <TabsList className="w-full">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="models">Models</TabsTrigger>
          <TabsTrigger value="languages">Languages</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">No-match text</h3>
            <p className="text-xs text-muted-foreground">What goes in front of the question when nothing matched, for every item that does not set its own</p>
            <Select value={noMatchLanguage ?? language} onValueChange={code => setNoMatchLanguage(code === language ? undefined : String(code))}>
              <SelectTrigger className="w-48"><span>{languageName(noMatchLanguage ?? language)}{!noMatchLanguage && " (default)"}</span></SelectTrigger>
              <SelectContent>
                <SelectItem value={language}>{languageName(language)} (default)</SelectItem>
                {languages.map(code => <SelectItem key={code} value={code}>{languageName(code)}</SelectItem>)}
              </SelectContent>
            </Select>
            <EditableText key={noMatchLanguage ?? language} value={localized(noMatch, "say", noMatchLanguage) ?? ""} placeholder={(noMatchLanguage && noMatch.say) || "No fallback message"}
              onChange={say => onChange({ ...playbook, defaults: { ...playbook.defaults, on_no_match: localize(noMatch, "say", say, noMatchLanguage) } })} />
          </div>
        </TabsContent>
        <TabsContent value="models" className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Model</h3>
            <p className="text-xs text-muted-foreground">What every ai item of this playbook runs on</p>
            <ModelSelect value={playbook.config?.model} onChange={model => setConfig({ model })} />
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Classify model</h3>
            <p className="text-xs text-muted-foreground">What picks an option from free text, when an item understands it. Usually something cheaper</p>
            <ModelSelect value={playbook.config?.classify_model} onChange={classify_model => setConfig({ classify_model })} placeholder="The playbook's model" />
          </div>
        </TabsContent>
        <TabsContent value="languages" className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Default language</h3>
            <p className="text-xs text-muted-foreground">What the texts of the playbook are written in. Changing it relabels them, it does not translate them</p>
            <div className="flex items-center gap-2">
              <Input value={defaultDraft ?? language} onChange={e => setDefaultDraft(e.target.value)} onBlur={commitDefault}
                onKeyDown={e => { if (e.key === "Enter") commitDefault() }} className="w-32" />
              <span className="text-sm text-muted-foreground">{languageName(language)}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium">Supported languages</h3>
            <p className="text-xs text-muted-foreground">The other languages it is translated to. A missing translation falls back to the default</p>
            {!!languages.length && <div className="flex flex-wrap gap-1">
              {languages.map(code => <Badge key={code} variant="secondary" className="gap-1">
                {languageName(code)} <span className="text-muted-foreground">{code}</span>
                <button type="button" aria-label={`Remove ${code}`} title="Remove this language" onClick={() => setConfig({ languages: nonEmpty(languages.filter(l => l !== code)) })}>
                  <XIcon className="size-3" />
                </button>
              </Badge>)}
            </div>}
            <form onSubmit={e => { e.preventDefault(); add() }} className="flex items-center gap-2">
              <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Language code, like es or fr-CA" className="w-56" />
              <Button type="submit" variant="outline" size="sm" disabled={!canAdd}>Add</Button>
              {draft.trim() && <span className="text-sm text-muted-foreground">{added ? languageName(added) : "Not a language code"}</span>}
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
}
